import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useSelector} from 'react-redux';
import {selectUserInfo} from 'src/selectors/crm';

import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Rating} from '@spaced-out/ui-design-system/lib/components/Rating';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {convertFromRaw} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';

import {getApiPrefix} from 'src/utils/crm';

import {formatMomentDateWithFormat} from 'src/utils/date-time';

import css from './view-custom-fields.css';
import editorCss from 'src/components/crm/jobs/job-info/about/details.css';


const customRenderOptions = {
  blockRenderers: {
    // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
    unstyled: (block) => `<span>${block.getText()}</span>`,
  },
  defaultBlockTag: 'span',
} as const;

const removeEmptyBlocks = (rawContent: any) => {
  rawContent.blocks = rawContent.blocks.filter(
    // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
    (block) => block.text.trim() !== '',
  );
  return rawContent;
};

const renderAsHTML = (jsonString: string) => {
  if (!jsonString) {
    return '';
  }
  try {
    const rawContent = JSON.parse(jsonString);
    const cleanedContent = removeEmptyBlocks(rawContent);
    const contentState = convertFromRaw(cleanedContent);
    return stateToHTML(contentState, customRenderOptions);
  } catch (error: any) {
    return jsonString || '';
  }
};
export const numberByFormat = (
  value: string | number,
  format: string = 'thousand',
): string | number => {
  if (!value) {
    return '';
  }
  switch (format) {
    case 'lakh':
      // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
      return parseFloat(value).toLocaleString('en-IN');
    case 'thousand':
      // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
      return parseFloat(value).toLocaleString();
    case 'NONE':
      // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
      return parseFloat(value);
    default:
      // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
      return parseFloat(value).toLocaleString();
  }
};

type CustomField = {
  id: number;
  fieldType: string;
  questionTag: string;
  value: string;
  placeholderFormat?: string;
  forPdf?: boolean;
  options: string[];
  customFieldId: number;
  users: {
    fullName: string;
    id: number;
  }[];
};

const ViewCustomFields = ({field}: {field: CustomField}) => {
  const {value, fieldType, placeholderFormat, users, forPdf, options} = field;
  const {user: userInfo} = useSelector(selectUserInfo) ?? {};
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  if (
    ['TEXT', 'SINGLE_SELECT', 'BOOLEAN', 'MULTI_SELECT', 'NUMBER'].includes(
      fieldType,
    )
  ) {
    return <BodyMedium>{value}</BodyMedium>;
  }
  if (fieldType === 'CURRENCY') {
    return <BodyMedium>{numberByFormat(value, placeholderFormat)}</BodyMedium>;
  }
  if (['SINGLE_USER_SELECT', 'MULTI_USER_SELECT'].includes(fieldType)) {
    const usersString =
      !users || users.length === 0
        ? t('NA', 'N/A')
        : users.map((user) => user.fullName).join(', ');
    return <BodyMedium>{usersString}</BodyMedium>;
  }

  if (['NUMBER_RANGE', 'CURRENCY_RANGE'].includes(fieldType)) {
    if (!value) {
      return <BodyMedium>{t('NA', 'N/A')}</BodyMedium>;
    }
    const valuesNum = value.split(',');
    if (!valuesNum || valuesNum.length !== 2) {
      return <BodyMedium>{t('NA', 'N/A')}</BodyMedium>;
    }
    const rangeString = `${numberByFormat(
      valuesNum[0],
      placeholderFormat,
    )} - ${numberByFormat(valuesNum[1], placeholderFormat)}`;

    return <BodyMedium>{rangeString}</BodyMedium>;
  }

  if (fieldType === 'DATE') {
    if (!value) {
      return <BodyMedium>{t('NA', 'N/A')}</BodyMedium>;
    }
    const values = parseInt(value, 10);
    return (
      <Tooltip
        // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'.
        title={formatMomentDateWithFormat(values, 'dddd, MMMM D, YYYY h:mm A')}
        elevation="modal"
      >
        <BodyMedium className={css.dateRangeText}>
          {/* @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'. */}
          {formatMomentDateWithFormat(values)}
        </BodyMedium>
      </Tooltip>
    );
  }
  if (fieldType === 'DATE_RANGE') {
    if (!value) {
      return <BodyMedium>{t('NA', 'N/A')}</BodyMedium>;
    }
    const valuesRange = value.split(',');
    return (
      <>
        <BodyMedium className={css.dateRangeText}>
          {valuesRange[0]} -
        </BodyMedium>

        <BodyMedium className={css.dateRangeText}>{valuesRange[1]}</BodyMedium>
      </>
    );
  }

  if (fieldType === 'RATING') {
    if (forPdf) {
      return <BodyMedium>{`${value}/5`}</BodyMedium>;
    }
    return <Rating readOnly rating={Number(value)} />;
  }

  if (fieldType === 'TEXT_AREA') {
    const htmlValue = renderAsHTML(value);
    return (
      <div
        className={editorCss.richTextEditor}
        dangerouslySetInnerHTML={{__html: htmlValue}}
      />
    );
  }

  if (fieldType === 'PARAMETERIZED_RATING') {
    return (
      <Card classNames={{wrapper: css.card}}>
        {options.map((option, idx) => {
          const values = value.split(`,`).map((x) => Number(x));
          if (forPdf) {
            return <BodyMedium>{`${option}: ${values[idx]} / 5`}</BodyMedium>;
          }
          return (
            <div>
              <BodyMedium>{option}</BodyMedium>
              <Rating readOnly rating={Number(values[idx])} />
            </div>
          );
        })}
      </Card>
    );
  }

  if (fieldType === 'SINGLE_ATTACHMENT') {
    if (!value || value === '-') {
      return <BodyMedium>-</BodyMedium>;
    }

    const link = `${getApiPrefix(
      userInfo?.roleTitle || '',
    )}/api/attachment-file?fileKey=${value}`;

    const handleOnClick = () => {
      window.open(window.encodeURI(link));
    };

    return (
      <div className={css.linkContainer}>
        <Link as="bodyMedium" onClick={handleOnClick}>
          <TruncatedTextWithTooltip
            tooltip={{
              body: value.split('__')[2],
              bodyMaxLines: 2,
            }}
          >
            {value.split('__')[2]}
          </TruncatedTextWithTooltip>
        </Link>
      </div>
    );
  }

  if (fieldType === 'MULTIPLE_ATTACHMENT') {
    if (!value || value === '-') {
      return <BodyMedium>-</BodyMedium>;
    }

    const fileKeys = value
      .split(',')
      .map((key: string) => key.trim())
      .filter(Boolean);
    if (fileKeys.length === 0) {
      return <BodyMedium>-</BodyMedium>;
    }

    return (
      <div className={css.linkContainer}>
        {fileKeys.map((fileKey: string) => {
          const link = `${getApiPrefix(
            userInfo?.roleTitle || '',
          )}/api/attachment-file?fileKey=${fileKey}`;

          const handleOnClick = () => {
            window.open(window.encodeURI(link));
          };

          const fileName = fileKey.split('__')[2] || fileKey;

          return (
            <div key={fileKey} className={css.multiAttachmentItem}>
              <Link as="bodyMedium" onClick={handleOnClick}>
                <TruncatedTextWithTooltip
                  tooltip={{
                    body: fileName,
                    bodyMaxLines: 2,
                  }}
                >
                  {fileName}
                </TruncatedTextWithTooltip>
              </Link>
            </div>
          );
        })}
      </div>
    );
  }

  return <BodyMedium>{t('NA', 'N/A')}</BodyMedium>;
};

export default ViewCustomFields;
